import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, ShieldAlert, AlertCircle, GraduationCap, CalendarDays, RefreshCw, Search, Loader2,
  UserCog, Plus, ChevronRight, ClipboardList, Award,
  ExternalLink, EyeOff, RotateCcw, X, Database, ArrowRight, AtSign,
} from "lucide-react";
import { toast } from "sonner";
import { EmployeePerformanceTab } from "@/components/hr/EmployeePerformanceTab";
import { RemunerationTab } from "@/components/hr/RemunerationTab";
import { DataQualityTab } from "@/components/hr/DataQualityTab";
import EscalationsPanel from "@/components/hr/EscalationsPanel";
import InductionCatalog from "@/components/hr/InductionCatalog";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    const rel = days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
    return `${d.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} (${rel})`;
  } catch { return String(iso).slice(0, 10); }
};

function KpiCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const border = tone === "warn"    ? "border-amber-200 bg-amber-50/40"
              : tone === "good"     ? "border-emerald-200 bg-emerald-50/40"
              : tone === "danger"   ? "border-rose-200 bg-rose-50/40"
              :                       "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${border} p-4`}>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function ExpiryBadge({ dateStr }) {
  if (!dateStr) return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">No expiry</Badge>;
  const d = new Date(dateStr);
  const now = new Date();
  const days = Math.round((d - now) / 86400000);
  if (days < 0) return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Expires in {days}d</Badge>;
  if (days <= 90) return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">Expires in {days}d</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Current</Badge>;
}

export default function HRHubPage() {
  const { role: userRole } = usePermissions();
  const role = (userRole || "").toLowerCase();
  const isFieldWorker = role === "field_worker";
  const canEdit = role === "executive" || role === "admin" || role === "finance_admin" || role === "hr" || role === "bu_manager";

  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [licences, setLicences] = useState([]);
  const [certs, setCerts] = useState([]);
  const [inductions, setInductions] = useState([]);
  const [inductionLinks, setInductionLinks] = useState({});
  const [unmatched, setUnmatched] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    if (isFieldWorker && (tab === "overview" || tab === "employees")) setTab("licences");
  }, [isFieldWorker, tab]);
  const [q, setQ] = useState("");
  const [empStatus, setEmpStatus] = useState("active");
  const [refreshingReg, setRefreshingReg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [aliasInput, setAliasInput] = useState("");

  const saveAliases = async (nextAliases) => {
    if (!detail) return;
    try {
      const { data } = await api.put(`/hr-hub/employees/${detail.id}/aliases`, { aliases: nextAliases });
      setDetail((d) => ({ ...d, aliases: data.aliases }));
      toast.success("Aliases updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update aliases");
    }
  };
  const addAlias = () => {
    const v = aliasInput.trim();
    if (!v) return;
    const next = [...(detail.aliases || []), v];
    setAliasInput("");
    saveAliases(next);
  };
  const removeAlias = (a) => saveAliases((detail.aliases || []).filter((x) => x !== a));

  const setStatus = async (active) => {
    if (!detail) return;
    try {
      await api.put(`/hr-hub/employees/${detail.id}/status`, { active });
      setDetail((d) => ({ ...d, active }));
      setEmployees((prev) => prev.map((e) => e.id === detail.id ? { ...e, active } : e));
      toast.success(active ? "Marked active" : "Marked inactive");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, c] = await Promise.all([
        api.get("/hr-hub/summary"),
        api.get("/hr-hub/licences?limit=5000"),
        api.get("/hr-hub/certifications?limit=5000"),
      ]);
      setSummary(s.data);
      setLicences(l.data.items || []);
      setCerts(c.data.items || []);
      api.get("/hr-hub/inductions?limit=5000").then((r) => setInductions(r.data.items || [])).catch(() => {});
      api.get("/inductions/catalog").then((r) => setInductionLinks(
        Object.fromEntries((r.data.items || []).filter((i) => i.link).map((i) => [i.name, i.link])))).catch(() => {});
      if (!isFieldWorker) {
        api.get("/hr-hub/compliance/unmatched").then((r) => setUnmatched(r.data)).catch(() => {});
        api.get("/hr-hub/import-status").then((r) => setImportStatus(r.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load HR Hub");
    } finally { setLoading(false); }
  };

  const refreshComplianceStatus = () => {
    if (isFieldWorker) return;
    api.get("/hr-hub/compliance/unmatched").then((r) => setUnmatched(r.data)).catch(() => {});
    api.get("/hr-hub/import-status").then((r) => setImportStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    api.get(`/hr-hub/employees?limit=1000&status=${empStatus}`)
      .then((r) => setEmployees(r.data.items || []))
      .catch(() => toast.error("Failed to load employees"));
  }, [empStatus]);

  const refreshRegister = async () => {
    setRefreshingReg(true);
    try {
      const { data } = await api.post("/hr/employee-performance/refresh-register");
      toast.success(`Register refreshed — ${data.employees_created} created, ${data.employees_updated} updated, ${data.employees_purged_demo} demo rows removed`);
      const r = await api.get(`/hr-hub/employees?limit=1000&status=${empStatus}`);
      setEmployees(r.data.items || []);
      const s = await api.get("/hr-hub/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Register refresh failed");
    } finally { setRefreshingReg(false); }
  };

  const filteredEmps = useMemo(() => {
    if (!q) return employees;
    const s = q.toLowerCase();
    return employees.filter((e) =>
      `${e.first_name} ${e.last_name} ${e.employee_number} ${e.role_title}`.toLowerCase().includes(s)
    );
  }, [employees, q]);
  const { sorted: sortedEmps, sort: empSort, setSort: setEmpSort } = useSortable(filteredEmps);

  const openDetail = async (emp) => {
    try {
      const r = await api.get(`/hr-hub/employees/${emp.id}`);
      setDetail(r.data);
    } catch (err) {
      toast.error("Failed to load employee");
    }
  };

  // demo seeding removed — data comes from AroFlo + Compliance Register

  // Overview lists: one row per (employee, item) — keep the soonest expiry.
  // The register legitimately holds the same item per client (e.g. General
  // Contractor Induction for ALDI + Coles), which looked like duplicates.
  const dedupeSoonest = (rows, itemField) => {
    const best = new Map();
    for (const r of rows) {
      const k = `${(r.employee_name || "").toLowerCase()}|${(r[itemField] || "").toLowerCase()}`;
      const exp = r.expiry_date || "9999-12-31";
      const cur = best.get(k);
      if (!cur || exp < (cur.expiry_date || "9999-12-31")) best.set(k, r);
    }
    return [...best.values()].sort(
      (a, b) => (a.expiry_date || "9999-12-31").localeCompare(b.expiry_date || "9999-12-31"));
  };
  const topLicences = dedupeSoonest(licences, "licence_type");
  const topCerts = dedupeSoonest(certs, "cert_name");

  return (
    <AppLayout
      title="HR & Workforce"
      subtitle="Employees · Licences · Certifications · Leave"
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="hr-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      }
    >
      {/* KPI strip */}
      {!isFieldWorker && (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <KpiCard label="Active employees" value={fmtNumber(summary?.total_active ?? 0)}
          sub={`${summary?.full_time ?? 0} FT (incl. unspecified) · ${summary?.part_time ?? 0} PT · ${summary?.casual ?? 0} casual`}
          icon={Users} testid="hr-kpi-active" />
        <KpiCard label="Licences" value={fmtNumber(summary?.licences_total ?? 0)}
          sub="Across all employees" icon={ShieldAlert} testid="hr-kpi-licences" />
        <KpiCard label="Licences expiring 30d" value={fmtNumber(summary?.licences_expiring_30d ?? 0)}
          sub={`${summary?.licences_expiring_90d ?? 0} within 90 days`} icon={AlertCircle}
          testid="hr-kpi-lic-30d"
          tone={(summary?.licences_expiring_30d ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard label="Licences expired" value={fmtNumber(summary?.licences_expired ?? 0)}
          icon={AlertCircle} testid="hr-kpi-lic-expired"
          tone={(summary?.licences_expired ?? 0) > 0 ? "danger" : "default"} />
        <KpiCard label="Certifications" value={fmtNumber(summary?.certifications_total ?? 0)}
          sub={`${summary?.certifications_expiring_30d ?? 0} expiring 30d`}
          icon={Award} testid="hr-kpi-certs" />
        <KpiCard label="Inductions" value={fmtNumber(summary?.inductions_total ?? 0)}
          sub={`${summary?.inductions_expiring_30d ?? 0} expiring 30d · ${summary?.inductions_expired ?? 0} expired`}
          icon={ShieldAlert} testid="hr-kpi-inductions"
          tone={(summary?.inductions_expiring_30d ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard label="At-risk employees" value={fmtNumber(summary?.employees_with_expiring_licences ?? 0)}
          sub="Licence expired or expiring in 30d" icon={UserCog}
          testid="hr-kpi-atrisk"
          tone={(summary?.employees_with_expiring_licences ?? 0) > 0 ? "warn" : "default"} />
      </div>
      )}

      {/* Last-import badge: when feeds were last imported + match stats */}
      {!isFieldWorker && importStatus && (importStatus.compliance?.rebuilt_at || importStatus.employees) && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-[11px]" data-testid="hr-last-import-badge">
          {importStatus.employees && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
              <Database className="w-3 h-3 text-slate-400" />
              Employees imported <b className="text-slate-800">{fmtWhen(importStatus.employees.uploaded_at)}</b>
              <span className="text-slate-400">· {fmtNumber(importStatus.employees.rows_inserted ?? 0)} new, {fmtNumber(importStatus.employees.rows_updated ?? 0)} updated</span>
            </span>
          )}
          {importStatus.compliance?.rebuilt_at && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
              <ShieldAlert className="w-3 h-3 text-slate-400" />
              Compliance rebuilt <b className="text-slate-800">{fmtWhen(importStatus.compliance.rebuilt_at)}</b>
              <span className="text-slate-400">
                · {fmtNumber((importStatus.compliance.licences ?? 0) + (importStatus.compliance.certifications ?? 0) + (importStatus.compliance.inductions ?? 0))} register rows
                {(importStatus.compliance.unmatched_names ?? 0) > 0 && <span className="text-amber-600"> · {fmtNumber(importStatus.compliance.unmatched_names)} unmatched</span>}
              </span>
            </span>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          {!isFieldWorker && <TabsTrigger data-testid="hr-tab-overview"      value="overview"       className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>}
          {!isFieldWorker && <TabsTrigger data-testid="hr-tab-employees"     value="employees"      className="text-xs data-[state=active]:bg-white">Employees</TabsTrigger>}
          <TabsTrigger data-testid="hr-tab-licences"      value="licences"       className="text-xs data-[state=active]:bg-white">{isFieldWorker ? "My Licences" : "Licences"}</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-certifications" value="certifications" className="text-xs data-[state=active]:bg-white">{isFieldWorker ? "My Certifications" : "Certifications"}</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-inductions"     value="inductions"     className="text-xs data-[state=active]:bg-white">{isFieldWorker ? "My Inductions" : "Inductions"}</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-induction-links" value="induction-links" className="text-xs data-[state=active]:bg-white">Inductions Links</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-performance"   value="performance"    className="text-xs data-[state=active]:bg-white">{isFieldWorker ? "My Performance" : "Employee Performance"}</TabsTrigger>
          {(role === "executive" || role === "finance_admin" || role === "bu_manager") && <TabsTrigger data-testid="hr-tab-remuneration" value="remuneration" className="text-xs data-[state=active]:bg-white">Remuneration</TabsTrigger>}
          {(role === "executive" || role === "admin" || role === "bu_manager") && <TabsTrigger data-testid="hr-tab-escalations" value="escalations" className="text-xs data-[state=active]:bg-white">Escalations</TabsTrigger>}
          {!isFieldWorker && <TabsTrigger data-testid="hr-tab-data-quality" value="data-quality" className="text-xs data-[state=active]:bg-white">Data Quality</TabsTrigger>}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" /> Top expiring / expired licences
              </div>
              {licences.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">No licences on file — import the Compliance Register via the Import Hub.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {topLicences.slice(0, 8).map((l) => (
                    <li key={l.id} className="py-2 flex items-center gap-2 text-sm">
                      <span className="text-slate-800 font-medium flex-1 truncate">{l.employee_name || "—"}</span>
                      <span className="text-slate-600 text-xs truncate max-w-[180px]">
                        {l.licence_type}
                        {l.client && l.client !== "Internal" && <span className="text-slate-400"> · {l.client}</span>}
                      </span>
                      <ExpiryBadge dateStr={l.expiry_date} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> Top expiring / expired certifications
              </div>
              {certs.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">No certifications yet.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {topCerts.slice(0, 8).map((c) => (
                    <li key={c.id} className="py-2 flex items-center gap-2 text-sm">
                      <span className="text-slate-800 font-medium flex-1 truncate">{c.employee_name || "—"}</span>
                      <span className="text-slate-600 text-xs truncate max-w-[180px]">
                        {c.cert_name}
                        {c.client && c.client !== "Internal" && <span className="text-slate-400"> · {c.client}</span>}
                      </span>
                      <ExpiryBadge dateStr={c.expiry_date} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Employees */}
        <TabsContent value="employees" className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative w-[300px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="hr-employees-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, number or role…" className="pl-9 h-9 bg-white" />
            </div>
            <Select value={empStatus} onValueChange={setEmpStatus}>
              <SelectTrigger className="h-9 w-[130px] bg-white text-xs" data-testid="hr-employees-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active" className="text-xs">Active</SelectItem>
                <SelectItem value="archived" className="text-xs">Archived</SelectItem>
                <SelectItem value="all" className="text-xs">All</SelectItem>
              </SelectContent>
            </Select>
            {canEdit && (
              <Button size="sm" variant="outline" className="h-9" disabled={refreshingReg}
                      data-testid="hr-refresh-register-btn" onClick={refreshRegister}>
                {refreshingReg ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Refresh from AroFlo
              </Button>
            )}
            <div className="ml-auto text-xs text-slate-500">{filteredEmps.length} of {employees.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <SortableTh label="Employee" k="last_name" sort={empSort} setSort={setEmpSort} />
                    <SortableTh label="Number" k="employee_number" sort={empSort} setSort={setEmpSort} />
                    <SortableTh label="Role" k="role_title" sort={empSort} setSort={setEmpSort} />
                    <SortableTh label="BU" k="business_unit_code" sort={empSort} setSort={setEmpSort} />
                    <SortableTh label="Employment" k="employment_type" sort={empSort} setSort={setEmpSort} />
                    <SortableTh label="Expiring lics" k="expiring_licences" sort={empSort} setSort={setEmpSort} className="text-right px-3 py-2 font-medium" />
                    <th className="text-right font-medium px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEmps.slice(0, 500).map((e) => (
                    <tr key={e.id} data-testid={`hr-emp-${e.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                        onClick={() => openDetail(e)}>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {e.first_name} {e.last_name}
                        {e.active === false && (
                          <Badge className="ml-2 bg-slate-100 text-slate-500 hover:bg-slate-100 text-[9px] uppercase">archived</Badge>
                        )}
                        {e.shared_email && (
                          <Badge className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[9px]"
                                 title={`Email ${e.email} is shared with another person (recycled email)`}
                                 data-testid={`hr-emp-shared-email-${e.id}`}>
                            <AtSign className="w-2.5 h-2.5 mr-0.5" />shared email
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{e.employee_number || "—"}</td>
                      <td className="px-3 py-2 text-slate-700">{e.role_title || "—"}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{e.business_unit_code || "—"}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{(e.employment_type || "").replace("_", " ")}</td>
                      <td className="px-3 py-2 text-right">
                        {e.expiring_licences > 0
                          ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">{e.expiring_licences}</Badge>
                          : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right"><ChevronRight className="w-3.5 h-3.5 text-slate-400 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Licences */}
        <TabsContent value="licences" className="mt-4">
          <LicenceTable rows={licences} testidPrefix="hr-lic" canEdit={canEdit} trackable coll="licences" searchColl="licences" />
        </TabsContent>

        {/* Certifications */}
        <TabsContent value="certifications" className="mt-4">
          <LicenceTable rows={certs} testidPrefix="hr-cert" isCert canEdit={canEdit} trackable coll="certifications" searchColl="certifications" />
        </TabsContent>

        {/* Inductions (personal / employee list) */}
        <TabsContent value="inductions" className="mt-4">
          {!isFieldWorker && unmatched?.count > 0 && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800"
                 data-testid="hr-unmatched-banner">
              <b>{unmatched.count} name{unmatched.count > 1 ? "s" : ""}</b> in the compliance register
              ({unmatched.rows_total} rows) don't match any employee — fix spelling in SharePoint and re-import:
              <span className="ml-1">{unmatched.items.slice(0, 8).map((u) => u.name).join(", ")}{unmatched.count > 8 ? "…" : ""}</span>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] ml-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                      data-testid="hr-unmatched-fix-btn" onClick={() => setTab("data-quality")}>
                Fix with alias tool <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
          <LicenceTable rows={inductions} testidPrefix="hr-induction" typeLabel="Induction" numberLabel="Client" linkMap={inductionLinks} canEdit={canEdit} trackable coll="inductions" searchColl="inductions" />
        </TabsContent>

        {/* Inductions Links (catalogue) */}
        <TabsContent value="induction-links" className="mt-4">
          <InductionCatalog canEdit={canEdit} onCatalog={setInductionLinks}
            onChanged={() => api.get("/hr-hub/inductions?limit=5000").then((r) => setInductions(r.data.items || [])).catch(() => {})} />
        </TabsContent>

        {/* Employee Performance */}
        {(role === "executive" || role === "finance_admin" || role === "bu_manager") && (
          <TabsContent value="remuneration" className="mt-4">
            <RemunerationTab canWrite={role === "executive" || role === "finance_admin" || role === "bu_manager"} isExec={role === "executive" || role === "bu_manager"} />
          </TabsContent>
        )}

        {(role === "executive" || role === "admin" || role === "finance_admin" || role === "bu_manager") && (
          <TabsContent value="escalations" className="mt-4">
            <EscalationsPanel />
          </TabsContent>
        )}

        <TabsContent value="performance" className="mt-4">
          <EmployeePerformanceTab canEdit={canEdit} />
        </TabsContent>

        {!isFieldWorker && (
          <TabsContent value="data-quality" className="mt-4">
            <DataQualityTab canEdit={canEdit} onComplianceChanged={refreshComplianceStatus} />
          </TabsContent>
        )}
      </Tabs>

      {/* Employee detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="hr-emp-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle>{detail.first_name} {detail.last_name}</SheetTitle>
                <SheetDescription>
                  {detail.role_title || "—"} · {detail.employee_number || "—"} · {detail.business_unit_code || "—"}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="rounded-md border border-slate-200 p-3 text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Details</div>
                  <dl className="space-y-1 text-xs">
                    <div className="flex justify-between"><dt className="text-slate-500">Email</dt>
                      <dd className="text-slate-800 flex items-center gap-1.5">
                        {detail.email || "—"}
                        {detail.shared_email && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[9px]"
                                 title="This email is shared with another person (recycled email)"
                                 data-testid="hr-emp-detail-shared-email">
                            <AtSign className="w-2.5 h-2.5 mr-0.5" />shared
                          </Badge>
                        )}
                      </dd>
                    </div>
                    <div className="flex justify-between"><dt className="text-slate-500">Employment</dt><dd className="text-slate-800">{(detail.employment_type || "").replace("_", " ")}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Start</dt><dd className="text-slate-800">{(detail.start_date || "—").slice(0, 10)}</dd></div>
                    <div className="flex justify-between items-center"><dt className="text-slate-500">Active</dt>
                      <dd>
                        {canEdit ? (
                          <div className="flex items-center gap-1.5" data-testid="hr-emp-status-toggle">
                            <button data-testid="hr-emp-status-active" onClick={() => setStatus(true)}
                              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${detail.active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>Active</button>
                            <button data-testid="hr-emp-status-inactive" onClick={() => setStatus(false)}
                              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${!detail.active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>Inactive</button>
                          </div>
                        ) : (detail.active ? "Yes" : "No")}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="rounded-md border border-slate-200 p-3" data-testid="hr-emp-aliases">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 flex items-center gap-1.5">
                    <UserCog className="w-3 h-3" /> Also known as
                  </div>
                  <p className="text-[11px] text-slate-400 mb-2">
                    Alternate name spellings (e.g. Joanna Carroll for Jo Carroll). Used to auto-merge records across AroFlo, SharePoint &amp; MYOB.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(detail.aliases || []).length === 0 && (
                      <span className="text-xs text-slate-400">No aliases yet.</span>
                    )}
                    {(detail.aliases || []).map((a) => (
                      <span key={a} data-testid={`hr-alias-${a}`}
                        className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1 py-0.5">
                        {a}
                        {canEdit && (
                          <button data-testid={`hr-alias-remove-${a}`} onClick={() => removeAlias(a)}
                            className="hover:text-rose-600 rounded-full p-0.5" title="Remove alias">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Input data-testid="hr-alias-input" value={aliasInput}
                        onChange={(e) => setAliasInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                        placeholder="Add another name…" className="h-8 text-xs" />
                      <Button size="sm" variant="outline" className="h-8" data-testid="hr-alias-add-btn"
                        onClick={addAlias} disabled={!aliasInput.trim()}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3" /> Licences ({detail.licences.length})
                  </div>
                  {detail.licences.length === 0 ? (
                    <div className="text-xs text-slate-500 py-2">None on file.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {detail.licences.map((l) => (
                        <li key={l.id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate">{l.licence_type}</span>
                          <ExpiryBadge dateStr={l.expiry_date} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1.5">
                    <Award className="w-3 h-3" /> Certifications ({detail.certifications.length})
                  </div>
                  {detail.certifications.length === 0 ? (
                    <div className="text-xs text-slate-500 py-2">None on file.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {detail.certifications.map((c) => (
                        <li key={c.id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate">{c.cert_name}</span>
                          <ExpiryBadge dateStr={c.expiry_date} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3" /> Inductions ({(detail.inductions || []).length})
                  </div>
                  {(detail.inductions || []).length === 0 ? (
                    <div className="text-xs text-slate-500 py-2">None on file.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {detail.inductions.map((i) => (
                        <li key={i.id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate">{i.induction_name}{i.client ? ` · ${i.client}` : ""}</span>
                          <ExpiryBadge dateStr={i.expiry_date} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {detail.leave_balance && (
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1.5">
                      <CalendarDays className="w-3 h-3" /> Leave balance
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><div className="text-slate-500">Annual</div><div className="font-semibold text-slate-900">{detail.leave_balance.annual_balance_hrs}h</div></div>
                      <div><div className="text-slate-500">Sick</div><div className="font-semibold text-slate-900">{detail.leave_balance.sick_balance_hrs}h</div></div>
                      <div><div className="text-slate-500">Personal</div><div className="font-semibold text-slate-900">{detail.leave_balance.personal_balance_hrs}h</div></div>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">As at {(detail.leave_balance.as_at || "").slice(0, 10)}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Seed demo dialog removed — data comes from AroFlo + Compliance Register */}
    </AppLayout>
  );
}

function LicenceTable({ rows, testidPrefix, isCert = false, typeLabel, numberLabel, linkMap,
                        canEdit = false, trackable = false, coll, searchColl }) {
  const [q, setQ] = useState("");
  const [view, setView] = useState("all"); // all | current | expired | inactive
  const [localRows, setLocalRows] = useState(rows);
  const [serverRows, setServerRows] = useState(null); // non-null when a server search is active
  const [searching, setSearching] = useState(false);
  const [inactiveRows, setInactiveRows] = useState(null); // lazy-loaded on first open of the Inactive view
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [restoring, setRestoring] = useState(false);
  const searchTimer = useRef(null);
  useEffect(() => { setLocalRows(rows); }, [rows]);
  useEffect(() => { setSelected(new Set()); }, [view]);

  const isExcluded = (r) => r.compliance_tracked === false;
  const isStatusInactive = (r) => /inactive/i.test(r.status || "");
  // Why is a row hidden? Non Renewing is checked first because those rows carry
  // BOTH status "Non Renewing" and compliance_tracked=false.
  const inactiveReason = (r) => {
    if (/non.?renew/i.test(r.status || "")) return "Non Renewing";
    if (isStatusInactive(r)) return "Status: Inactive";
    if (isExcluded(r)) return "Manually excluded";
    return null;
  };
  // Only compliance_tracked=false rows can be restored in the hub. A pure
  // Status=Inactive row comes from SharePoint and can't be un-inactivated here.
  const canRestore = (r) => isExcluded(r);

  // Lazy-load the inactive set (Status=Inactive OR excluded) the first time the
  // Inactive view is opened; these are hidden from the default list/search.
  useEffect(() => {
    if (view !== "inactive" || inactiveRows !== null || !coll) return;
    setLoadingInactive(true);
    api.get(`/hr-hub/${coll}?limit=5000&scope=inactive`)
      .then(({ data }) => setInactiveRows(data.items || []))
      .catch(() => setInactiveRows([]))
      .finally(() => setLoadingInactive(false));
  }, [view, inactiveRows, coll]);

  // Server-side search over the ACTIVE dataset (default scope already hides
  // inactive rows). In the Inactive view we search client-side instead.
  useEffect(() => {
    if (!searchColl || view === "inactive") { return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const needle = q.trim();
    if (!needle) { setServerRows(null); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/hr-hub/${searchColl}?limit=1000&q=${encodeURIComponent(needle)}`);
        setServerRows(data.items || []);
      } catch { setServerRows([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [q, searchColl, view]);

  // Keep server-search results in sync with optimistic tracking toggles.
  const baseRows = view === "inactive"
    ? (inactiveRows || [])
    : (serverRows !== null ? serverRows : localRows);

  const todayIso = new Date().toISOString().slice(0, 10);
  const isExpired = (r) => {
    const e = (r.expiry_date || "").slice(0, 10);
    return e && e < "2098" && e < todayIso;
  };
  const isTracked = (r) => r.compliance_tracked !== false;

  // Exclude/Restore moves the row between the active and inactive lists. A row
  // that is Status=Inactive stays inactive even when re-tracked (SharePoint is
  // the source of truth for that), so we only move it back if re-tracking
  // actually makes it active again.
  const toggleTrack = async (r) => {
    const next = !isTracked(r); // next compliance_tracked state
    const updated = { ...r, compliance_tracked: next };
    const stillInactive = next ? isStatusInactive(updated) : true;
    // optimistic move
    if (view === "inactive") {
      setInactiveRows((prev) => {
        const rest = (prev || []).filter((x) => x.id !== r.id);
        return stillInactive ? [updated, ...rest] : rest;
      });
      if (!stillInactive) setLocalRows((prev) => [updated, ...prev]);
    } else {
      setLocalRows((prev) => prev.filter((x) => x.id !== r.id));
      setServerRows((prev) => prev === null ? prev : prev.filter((x) => x.id !== r.id));
      setInactiveRows((prev) => prev === null ? prev : [updated, ...prev]);
    }
    try {
      await api.patch(`/hr-hub/${coll}/${r.id}/tracking`, { tracked: next });
      toast.success(next ? "Restored to active register" : "Marked inactive (hidden from search)");
    } catch (e) {
      // revert: drop our optimistic copies and refetch the inactive set
      setLocalRows(rows);
      setServerRows(null);
      setInactiveRows(null);
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const bulkSetTracking = async (tracked) => {
    const ids = [...selected];
    if (!ids.length) return;
    setRestoring(true);
    if (tracked) {
      // Restore: move picked rows out of the inactive list; those that become
      // active again (not Status=Inactive) go back into the active list.
      const byId = new Map((inactiveRows || []).map((r) => [r.id, r]));
      const picked = ids.map((id) => byId.get(id)).filter(Boolean);
      setInactiveRows((prev) => (prev || []).filter((r) => !selected.has(r.id)));
      const nowActive = picked.map((r) => ({ ...r, compliance_tracked: true })).filter((r) => !isStatusInactive(r));
      if (nowActive.length) setLocalRows((prev) => [...nowActive, ...prev]);
    } else {
      // Exclude: move picked active rows into the inactive list.
      const base = serverRows !== null ? serverRows : localRows;
      const byId = new Map(base.map((r) => [r.id, r]));
      const picked = ids.map((id) => byId.get(id)).filter(Boolean).map((r) => ({ ...r, compliance_tracked: false }));
      setLocalRows((prev) => prev.filter((r) => !selected.has(r.id)));
      setServerRows((prev) => prev === null ? prev : prev.filter((r) => !selected.has(r.id)));
      setInactiveRows((prev) => prev === null ? prev : [...picked, ...prev]);
    }
    setSelected(new Set());
    try {
      await api.patch("/hr-hub/compliance/tracking/bulk", { collection: coll, ids, tracked });
      toast.success(`${tracked ? "Restored" : "Excluded"} ${ids.length} item${ids.length === 1 ? "" : "s"}`);
    } catch (e) {
      setInactiveRows(null); // force clean refetch on failure
      setLocalRows(rows);
      setServerRows(null);
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setRestoring(false);
    }
  };

  const filtered = useMemo(() => {
    const serverActive = view !== "inactive" && serverRows !== null;
    const needle = q.trim().toLowerCase();
    return baseRows.filter((l) => {
      if (view === "current" && (isExpired(l) || !isTracked(l))) return false;
      if (view === "expired" && (!isExpired(l) || !isTracked(l))) return false;
      // Server already applied the text filter; skip the client-side name match.
      if (serverActive || !needle) return true;
      return [l.employee_name, l.licence_type, l.cert_name, l.licence_number, l.issuer]
        .some((v) => (v || "").toLowerCase().includes(needle));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRows, serverRows, inactiveRows, q, view]);
  const { sorted: sortedLics, sort: licSort, setSort: setLicSort } = useSortable(filtered);
  if (rows.length === 0 && serverRows === null && view !== "inactive") {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
        No {isCert ? "certifications" : "licences"} on file. Import the Compliance Register via the Import Hub to populate.
      </div>
    );
  }
  const inactiveCount = inactiveRows?.length;
  const selectableIds = (view === "inactive" ? filtered.filter(canRestore) : filtered).map((r) => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(() => allSelected ? new Set() : new Set(selectableIds));
  const showSelectCol = trackable && canEdit;
  const rowSelectable = (l) => view === "inactive" ? canRestore(l) : true;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60 flex-wrap">
        <Search className="w-3.5 h-3.5 text-slate-400" />
        <input
          data-testid={`${testidPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search employee, ${(typeLabel || (isCert ? "certification" : "licence")).toLowerCase()}, ${(numberLabel || (isCert ? "issuer" : "number")).toLowerCase()}…`}
          className="flex-1 min-w-[160px] max-w-md bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
        {trackable && (
          <div className="flex items-center gap-1" data-testid={`${testidPrefix}-view-filter`}>
            {[
              { k: "all", label: "All" },
              { k: "current", label: "Current" },
              { k: "expired", label: "Expired" },
              { k: "inactive", label: `Inactive${inactiveCount ? ` (${inactiveCount})` : ""}` },
            ].map((v) => (
              <button key={v.k} data-testid={`${testidPrefix}-view-${v.k}`}
                onClick={() => setView(v.k)}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${
                  view === v.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                {v.label}
              </button>
            ))}
          </div>
        )}
        {showSelectCol && selected.size > 0 && (
          view === "inactive" ? (
            <Button size="sm"
              data-testid={`${testidPrefix}-bulk-restore`}
              disabled={restoring}
              className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => bulkSetTracking(true)}>
              {restoring
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Restoring…</>
                : <><RotateCcw className="w-3.5 h-3.5 mr-1" />Restore selected ({selected.size})</>}
            </Button>
          ) : (
            <Button size="sm"
              data-testid={`${testidPrefix}-bulk-exclude`}
              disabled={restoring}
              className="h-7 text-[11px] bg-slate-700 hover:bg-slate-800 text-white"
              onClick={() => bulkSetTracking(false)}>
              {restoring
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Excluding…</>
                : <><EyeOff className="w-3.5 h-3.5 mr-1" />Exclude selected ({selected.size})</>}
            </Button>
          )
        )}
        <span className="text-[10px] text-slate-400 whitespace-nowrap ml-auto">
          {searching || (view === "inactive" && loadingInactive)
            ? "loading…"
            : (view === "inactive" || serverRows !== null)
              ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}`
              : `${filtered.length} of ${rows.length}`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {showSelectCol && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" data-testid={`${testidPrefix}-select-all`}
                    checked={allSelected} onChange={toggleSelectAll}
                    disabled={selectableIds.length === 0}
                    className="accent-emerald-600 cursor-pointer disabled:opacity-40" />
                </th>
              )}
              <SortableTh label="Employee" k="employee_name" sort={licSort} setSort={setLicSort} />
              <SortableTh label={typeLabel || (isCert ? "Certification" : "Licence type")} k={isCert ? "cert_name" : "licence_type"} sort={licSort} setSort={setLicSort} />
              <SortableTh label={numberLabel || (isCert ? "Issuer" : "Number")} k={isCert ? "issuer" : "licence_number"} sort={licSort} setSort={setLicSort} />
              <SortableTh label="Expiry" k="expiry_date" sort={licSort} setSort={setLicSort} />
              <th className="text-left font-medium px-3 py-2">Document</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
              {trackable && canEdit && <th className="text-right font-medium px-3 py-2">Compliance</th>}
            </tr>
          </thead>
          <tbody>
            {sortedLics.slice(0, 5000).map((l) => {
              const tracked = isTracked(l);
              const statusInactive = isStatusInactive(l);
              const inactiveRow = !tracked || statusInactive;
              const reason = inactiveReason(l);
              return (
              <tr key={l.id} data-testid={`${testidPrefix}-${l.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50/60 ${inactiveRow ? "bg-slate-50/40 text-slate-400" : ""}`}>
                {showSelectCol && (
                  <td className="px-3 py-2">
                    {rowSelectable(l) ? (
                      <input type="checkbox" data-testid={`${testidPrefix}-select-${l.id}`}
                        checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)}
                        className="accent-emerald-600 cursor-pointer" />
                    ) : null}
                  </td>
                )}
                <td className="px-3 py-2 font-medium">{l.employee_name || "—"}</td>
                <td className="px-3 py-2">
                  {l.licence_type || l.cert_name || "—"}
                  {linkMap?.[l.licence_type] && (
                    <a href={linkMap[l.licence_type]} target="_blank" rel="noopener noreferrer"
                       data-testid={`${testidPrefix}-link-${l.id}`} title="Open induction"
                       className="inline-flex align-middle ml-1.5 text-gbe/60 hover:text-gbe">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{isCert ? (l.issuer || "—") : (l.licence_number || "—")}</td>
                <td className="px-3 py-2 text-xs">{!l.expiry_date || l.expiry_date >= "2098" ? "No expiry" : l.expiry_date.slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs max-w-[220px]" data-testid={`${testidPrefix}-doc-${l.id}`}>
                  {l.document_name
                    ? <span className="truncate block text-slate-600" title={l.document_name}>{l.document_name}</span>
                    : <span className="text-amber-600">— none —</span>}
                </td>
                <td className="px-3 py-2">
                  {inactiveRow && reason
                    ? <Badge data-testid={`${testidPrefix}-reason-${l.id}`}
                        className={`text-[10px] hover:opacity-100 ${reason === "Non Renewing"
                          ? "bg-amber-100 text-amber-700 hover:bg-amber-100"
                          : "bg-slate-200 text-slate-500 hover:bg-slate-200"}`}>{reason}</Badge>
                    : <ExpiryBadge dateStr={l.expiry_date} />}
                </td>
                {trackable && canEdit && (
                  <td className="px-3 py-2 text-right">
                    {statusInactive && tracked ? (
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">From register</span>
                    ) : (
                      <Button size="sm" variant="ghost"
                        data-testid={`${testidPrefix}-track-${l.id}`}
                        className="h-7 text-[11px] text-slate-500 hover:text-slate-900"
                        onClick={() => toggleTrack(l)}>
                        {tracked
                          ? <><EyeOff className="w-3.5 h-3.5 mr-1" />Exclude</>
                          : <><RotateCcw className="w-3.5 h-3.5 mr-1" />Restore</>}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ); })}
          </tbody>
        </table>
        {sortedLics.length > 500 && (
          <div className="text-[10px] text-slate-400 px-3 py-1.5 border-t border-slate-100">
            Showing 500 of {sortedLics.length} rows — search or sort to surface others
          </div>
        )}
      </div>
    </div>
  );
}
